import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../api/api';
import MultiMetricChart from './MultiMetricChart';
import { formatDisplayDate } from '../utils/DateUtil';
import './ActivityDetail.css';

const STREAM_METRICS = {
    pace: { label: 'Pace', colour: '#1769aa', unit: 'min/km', field: 'velocitySmooth', reverse: true, minValue: 2, maxValue: 30 },
    heartrate: { label: 'Heart rate', colour: '#c43c3c', unit: 'bpm', field: 'heartrate' },
    cadence: { label: 'Cadence', colour: '#d35f8d', unit: 'spm', field: 'cadence', transform: (value) => value * 2 },
    altitude: { label: 'Elevation', colour: '#73777a', unit: 'm', field: 'altitude' },
    elevation_gain: { label: 'Elevation gain', colour: '#73777a', unit: 'm', selectionAggregation: 'sum' },
    grade_percent: { label: 'Grade', colour: '#258277', unit: '%', selectionAggregation: 'grade_percent', allowNegative: true },
};
const normalizeStreamMetric = (metric) => metric;

const formatDate = (value) => value
    ? `${formatDisplayDate(value)}, ${new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
    : 'Unknown date';
const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return '—';
    const totalMinutes = Math.round(seconds / 60);
    return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
};
const formatDistance = (metres) => metres === null || metres === undefined ? '—' : `${(metres / 1000).toFixed(2)} km`;
const formatWhole = (value, unit) => value === null || value === undefined ? '—' : `${Math.round(value).toLocaleString()} ${unit}`;
const formatPaceFromSpeed = (speed) => {
    if (!speed || speed <= 0) return '—';
    const totalSeconds = Math.round(1000 / speed);
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')} min/km`;
};
const streamMetricValue = (sample, metric) => {
    const config = STREAM_METRICS[metric];
    if (!config?.field) return null;
    const value = sample[config.field];
    if (value === null || value === undefined) return null;
    if (metric === 'pace' && sample.moving === false) return null;
    const metricValue = metric === 'pace'
        ? value > 0 ? 1000 / value / 60 : null
        : config.transform ? config.transform(value) : value;
    if (metricValue === null) return null;
    if (config.minValue !== undefined && metricValue < config.minValue) return null;
    if (config.maxValue !== undefined && metricValue > config.maxValue) return null;
    return metricValue;
};
const formatStreamValue = (value, metric) => {
    if (value === null || value === undefined) return '—';
    if (metric === 'pace') {
        const totalSeconds = Math.round(value * 60);
        return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')} min/km`;
    }
    // A bucketed elevation-gain sample is often well under a metre - fine
    // for the running total shown in the stats panel, but rounded to a
    // whole number here it reads as a flat 0 for most of the chart, same as
    // grade already needed a decimal to be meaningful.
    const precision = metric === 'grade_percent' || metric === 'elevation_gain' ? 1 : 0;
    return `${value.toFixed(precision)} ${STREAM_METRICS[metric].unit}`;
};
const formatStreamXAxisLabel = (point) => point.xValue > 1000 ? `${(point.xValue / 1000).toFixed(1)} km` : `${Math.round(point.xValue)}s`;
const streamXValue = (sample, index) => sample.distance ?? sample.time ?? index;

const elevationGainPoints = (streams, totalElevationGainMetres) => {
    let previousAltitude = null;
    const points = streams.map((sample, index) => {
        if (sample.altitude === null || sample.altitude === undefined) return null;
        const gain = previousAltitude !== null && sample.altitude > previousAltitude
            ? sample.altitude - previousAltitude
            : 0;
        previousAltitude = sample.altitude;
        return {
            xValue: streamXValue(sample, index),
            value: gain,
        };
    }).filter(Boolean);
    const streamGain = points.reduce((total, point) => total + point.value, 0);
    if (!totalElevationGainMetres || streamGain <= 0) return points;
    const scale = totalElevationGainMetres / streamGain;
    return points.map((point) => ({ ...point, value: point.value * scale }));
};

// Signed altitude change between samples. The gain-only version above is
// what an "elevation gained" total wants, but a gradient needs the
// descents as well, or every downhill stretch reads as a flat 0%. These
// deltas are also left unscaled - Strava's reported total is a climb
// figure, so scaling to it would skew the downhills.
const altitudeDeltaPoints = (streams) => {
    let previousAltitude = null;
    return streams.map((sample, index) => {
        if (sample.altitude === null || sample.altitude === undefined) return null;
        const delta = previousAltitude === null ? 0 : sample.altitude - previousAltitude;
        previousAltitude = sample.altitude;
        return {
            xValue: streamXValue(sample, index),
            value: delta,
        };
    }).filter(Boolean);
};

const gradePercentPoints = (streams) => {
    const gainPoints = altitudeDeltaPoints(streams);
    const bucketDistanceMetres = 100;
    let bucketStartXValue = null;
    let previousXValue = null;
    let bucketGain = 0;
    let bucketDistance = 0;
    const points = [];
    gainPoints.forEach((point) => {
        if (bucketStartXValue === null) bucketStartXValue = point.xValue;
        const distanceDeltaMetres = previousXValue === null ? 0 : Math.max(0, point.xValue - previousXValue);
        previousXValue = point.xValue;
        bucketGain += point.value;
        bucketDistance += distanceDeltaMetres;
        if (bucketDistance >= bucketDistanceMetres) {
            points.push({
                xValue: bucketStartXValue + bucketDistance / 2,
                value: bucketGain / (bucketDistance / 1000) / 10,
                gain: bucketGain,
                distanceDelta: bucketDistance,
            });
            bucketStartXValue = point.xValue;
            bucketGain = 0;
            bucketDistance = 0;
        }
    });
    if (bucketDistance > 0) {
        points.push({
            xValue: bucketStartXValue + bucketDistance / 2,
            value: bucketGain / (bucketDistance / 1000) / 10,
            gain: bucketGain,
            distanceDelta: bucketDistance,
        });
    }
    return points;
};

const ActivityRouteMap = ({ activity, highlightedXValue, onHoverXValue }) => {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0);
    const [mapDrag, setMapDrag] = useState(null);
    const mapHitAreaRef = useRef(null);
    // Active touch/pointer contacts on the map, keyed by pointerId - tracked
    // outside React state (a ref, updated synchronously on every
    // pointerdown/move/up) so a fast-arriving second finger is never missed
    // by a state update that hasn't landed yet.
    const activePointersRef = useRef(new Map());
    // Where the pinch gesture started (distance + angle between the two
    // fingers, and the zoom/rotation they began from) - null outside of a
    // two-finger touch.
    const pinchStartRef = useRef(null);
    const zoomMap = (zoomFactor) => setZoom((currentZoom) => Math.max(1, Math.min(currentZoom * zoomFactor, 6)));
    const activePointerDistanceAndAngle = () => {
        const points = Array.from(activePointersRef.current.values());
        if (points.length < 2) return null;
        const [first, second] = points;
        const deltaX = second.x - first.x;
        const deltaY = second.y - first.y;
        return { distance: Math.hypot(deltaX, deltaY), angle: Math.atan2(deltaY, deltaX) * 180 / Math.PI };
    };

    useEffect(() => {
        const mapHitArea = mapHitAreaRef.current;
        if (!mapHitArea) return undefined;
        const zoomMapFromWheel = (event) => {
            event.preventDefault();
            setZoom((currentZoom) => Math.max(1, Math.min(currentZoom * (event.deltaY < 0 ? 1.18 : 1 / 1.18), 6)));
        };
        mapHitArea.addEventListener('wheel', zoomMapFromWheel, { passive: false });
        // iOS Safari recognizes a two-finger touch as a page pinch-zoom via
        // its own non-standard gesture* events *before* our pointer-event
        // pinch handling ever gets a look-in - `touch-action: none` doesn't
        // reliably suppress those on iOS the way it does the standard touch
        // scroll/zoom gestures, so they're blocked explicitly here too.
        // Harmless (and simply never fires) on browsers with no such event.
        const blockNativeGesture = (event) => event.preventDefault();
        mapHitArea.addEventListener('gesturestart', blockNativeGesture);
        mapHitArea.addEventListener('gesturechange', blockNativeGesture);
        return () => {
            mapHitArea.removeEventListener('wheel', zoomMapFromWheel);
            mapHitArea.removeEventListener('gesturestart', blockNativeGesture);
            mapHitArea.removeEventListener('gesturechange', blockNativeGesture);
        };
    }, []);

    const routeSamples = (activity.streams || [])
        .filter((sample) => sample.latitude !== null && sample.latitude !== undefined && sample.longitude !== null && sample.longitude !== undefined);

    if (routeSamples.length < 2) {
        return <p className="activity-detail-empty">No map samples stored for this activity.</p>;
    }

    const meanLatitude = routeSamples.reduce((total, sample) => total + sample.latitude, 0) / routeSamples.length;
    const longitudeScale = Math.cos(meanLatitude * Math.PI / 180);
    const projectedSamples = routeSamples.map((sample, index) => ({
        ...sample,
        xValue: streamXValue(sample, index),
        projected: {
            x: sample.longitude * longitudeScale,
            y: sample.latitude,
        },
    }));

    const width = 760;
    const height = 320;
    const padding = 26;
    const minX = Math.min(...projectedSamples.map((sample) => sample.projected.x));
    const maxX = Math.max(...projectedSamples.map((sample) => sample.projected.x));
    const minY = Math.min(...projectedSamples.map((sample) => sample.projected.y));
    const maxY = Math.max(...projectedSamples.map((sample) => sample.projected.y));
    const routeWidth = maxX - minX || 1;
    const routeHeight = maxY - minY || 1;
    const scale = Math.min((width - padding * 2) / routeWidth, (height - padding * 2) / routeHeight) * zoom;
    const drawnWidth = routeWidth * scale;
    const drawnHeight = routeHeight * scale;
    const maxPanX = Math.max(0, (drawnWidth - (width - padding * 2)) / 2 + padding);
    const maxPanY = Math.max(0, (drawnHeight - (height - padding * 2)) / 2 + padding);
    const constrainedPan = {
        x: Math.max(-maxPanX, Math.min(maxPanX, pan.x)),
        y: Math.max(-maxPanY, Math.min(maxPanY, pan.y)),
    };
    const offsetX = (width - drawnWidth) / 2 + constrainedPan.x;
    const offsetY = (height - drawnHeight) / 2 + constrainedPan.y;
    const mapCenter = { x: width / 2, y: height / 2 };
    const rotatePoint = (point) => {
        const angle = rotation * Math.PI / 180;
        const deltaX = point.x - mapCenter.x;
        const deltaY = point.y - mapCenter.y;
        return {
            x: mapCenter.x + deltaX * Math.cos(angle) - deltaY * Math.sin(angle),
            y: mapCenter.y + deltaX * Math.sin(angle) + deltaY * Math.cos(angle),
        };
    };
    const pointForSample = (sample) => rotatePoint({
        x: offsetX + (sample.projected.x - minX) * scale,
        y: offsetY + (maxY - sample.projected.y) * scale,
    });
    const path = projectedSamples.map((sample, index) => {
        const point = pointForSample(sample);
        return `${index ? 'L' : 'M'} ${point.x} ${point.y}`;
    }).join(' ');
    const start = pointForSample(projectedSamples[0]);
    const finish = pointForSample(projectedSamples[projectedSamples.length - 1]);
    const highlightedSample = highlightedXValue === null || highlightedXValue === undefined ? null : projectedSamples.reduce((nearestSample, sample) => {
        return Math.abs(sample.xValue - highlightedXValue) < Math.abs(nearestSample.xValue - highlightedXValue)
            ? sample
            : nearestSample;
    }, projectedSamples[0]);
    const highlightedPoint = highlightedSample ? pointForSample(highlightedSample) : null;
    const directionMarkers = [0.2, 0.4, 0.6, 0.8].map((fraction) => {
        const index = Math.max(1, Math.min(projectedSamples.length - 2, Math.round(projectedSamples.length * fraction)));
        const previous = pointForSample(projectedSamples[index - 1]);
        const current = pointForSample(projectedSamples[index]);
        const next = pointForSample(projectedSamples[index + 1]);
        const angle = Math.atan2(next.y - previous.y, next.x - previous.x) * 180 / Math.PI;
        return { ...current, angle };
    });
    const setHighlightFromMapPointer = (event) => {
        if (mapDrag) return;
        const box = event.currentTarget.getBoundingClientRect();
        const pointer = {
            x: ((event.clientX - box.left) / box.width) * width,
            y: ((event.clientY - box.top) / box.height) * height,
        };
        const nearestSample = projectedSamples.reduce((nearest, sample) => {
            const point = pointForSample(sample);
            const nearestPoint = pointForSample(nearest);
            const distance = Math.hypot(point.x - pointer.x, point.y - pointer.y);
            const nearestDistance = Math.hypot(nearestPoint.x - pointer.x, nearestPoint.y - pointer.y);
            return distance < nearestDistance ? sample : nearest;
        }, projectedSamples[0]);
        onHoverXValue?.(nearestSample.xValue);
    };
    const startMapDrag = (event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        // A second finger landing turns this into a pinch: distance apart
        // drives zoom, the angle between the two fingers drives rotation -
        // mouse-only gestures (wheel zoom, right-click-drag rotate) have no
        // touch equivalent, so this is how both work on a phone/tablet.
        if (activePointersRef.current.size === 2) {
            const info = activePointerDistanceAndAngle();
            pinchStartRef.current = info && { ...info, zoom, rotation };
            setMapDrag(null);
            return;
        }
        if (activePointersRef.current.size > 2) return;
        setMapDrag({
            pointerId: event.pointerId,
            mode: event.button === 2 ? 'rotate' : 'pan',
            startClientX: event.clientX,
            startClientY: event.clientY,
            startPan: constrainedPan,
            startRotation: rotation,
        });
    };
    const updateMapDrag = (event) => {
        if (activePointersRef.current.has(event.pointerId)) {
            activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
        }
        if (activePointersRef.current.size === 2 && pinchStartRef.current) {
            const info = activePointerDistanceAndAngle();
            if (info && pinchStartRef.current.distance > 0) {
                setZoom(Math.max(1, Math.min(pinchStartRef.current.zoom * (info.distance / pinchStartRef.current.distance), 6)));
                setRotation(pinchStartRef.current.rotation + (info.angle - pinchStartRef.current.angle));
            }
            return;
        }
        if (!mapDrag || mapDrag.pointerId !== event.pointerId) {
            setHighlightFromMapPointer(event);
            return;
        }
        if (mapDrag.mode === 'rotate') {
            setRotation(mapDrag.startRotation + (event.clientX - mapDrag.startClientX) / 2);
            return;
        }
        const box = event.currentTarget.getBoundingClientRect();
        const nextPan = {
            x: mapDrag.startPan.x + ((event.clientX - mapDrag.startClientX) / box.width) * width,
            y: mapDrag.startPan.y + ((event.clientY - mapDrag.startClientY) / box.height) * height,
        };
        setPan({
            x: Math.max(-maxPanX, Math.min(maxPanX, nextPan.x)),
            y: Math.max(-maxPanY, Math.min(maxPanY, nextPan.y)),
        });
    };
    const finishMapDrag = (event) => {
        activePointersRef.current.delete(event.pointerId);
        if (activePointersRef.current.size < 2) {
            pinchStartRef.current = null;
        }
        // Dropping from two fingers to one shouldn't strand the remaining
        // finger with no gesture at all - hand it straight back to panning
        // from wherever it currently is, rather than requiring a full
        // lift-and-retouch to keep moving the map.
        if (activePointersRef.current.size === 1) {
            const [[remainingPointerId, remainingPoint]] = activePointersRef.current;
            setMapDrag({
                pointerId: remainingPointerId,
                mode: 'pan',
                startClientX: remainingPoint.x,
                startClientY: remainingPoint.y,
                startPan: constrainedPan,
                startRotation: rotation,
            });
        } else if (mapDrag && mapDrag.pointerId === event.pointerId) {
            setMapDrag(null);
        }
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };
    return (
        <div className="activity-route-map-shell">
            <div className="activity-route-map-controls" aria-label="Map zoom controls">
                <button type="button" aria-label="Zoom out" onClick={() => zoomMap(1 / 1.35)}>-</button>
                <button type="button" aria-label="Zoom in" onClick={() => zoomMap(1.35)}>+</button>
                <button type="button" aria-label="Reset compass" className="activity-route-map-compass" onClick={() => setRotation(0)}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" style={{ transform: `rotate(${-rotation}deg)` }}>
                        <path className="compass-north" d="M12 2 L17 12 L12 10 L7 12 Z" />
                        <path className="compass-south" d="M12 22 L7 12 L12 14 L17 12 Z" />
                    </svg>
                </button>
            </div>
            <svg className={`activity-route-map${mapDrag ? ` ${mapDrag.mode}` : ''}`} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Activity route map" onContextMenu={(event) => event.preventDefault()}>
                <defs>
                    <pattern id="route-map-grid" width="38" height="38" patternUnits="userSpaceOnUse">
                        <path d="M 38 0 L 0 0 0 38" fill="none" />
                    </pattern>
                </defs>
                <rect className="activity-route-map-background" x="0" y="0" width={width} height={height} />
                <rect className="activity-route-map-grid" x="0" y="0" width={width} height={height} fill="url(#route-map-grid)" />
                <path className="activity-route-map-path-shadow" d={path} />
                <path className="activity-route-map-path" d={path} />
                {directionMarkers.map((marker, index) => <path key={index} className="activity-route-map-direction" d="M -7 -5 L 8 0 L -7 5 Z" transform={`translate(${marker.x} ${marker.y}) rotate(${marker.angle})`} />)}
                <circle className="activity-route-map-start" cx={start.x} cy={start.y} r="6" />
                <circle className="activity-route-map-finish" cx={finish.x} cy={finish.y} r="6" />
                {highlightedPoint && <circle className="activity-route-map-highlight" cx={highlightedPoint.x} cy={highlightedPoint.y} r="8" />}
                <text x={start.x + 9} y={start.y - 8}>Start</text>
                <text x={finish.x + 9} y={finish.y + 15}>Finish</text>
                <rect ref={mapHitAreaRef} className="activity-route-map-hit-area" x="0" y="0" width={width} height={height} onPointerDown={startMapDrag} onPointerMove={updateMapDrag} onPointerUp={finishMapDrag} onPointerCancel={finishMapDrag} onPointerLeave={(event) => { finishMapDrag(event); onHoverXValue?.(null); }} />
            </svg>
        </div>
    );
};

const ActivityDetail = () => {
    const { activityId } = useParams();
    const [activity, setActivity] = useState(null);
    const [selectedMetrics, setSelectedMetrics] = useState(['pace', 'heartrate']);
    const [highlightedRouteXValue, setHighlightedRouteXValue] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        setIsLoading(true);
        setError('');
        api.getActivity(activityId)
            .then(({ data }) => setActivity(data))
            .catch((e) => {
                setActivity(null);
                setError(api.getErrorMessage(e, 'Unable to load activity.'));
            })
            .finally(() => setIsLoading(false));
    }, [activityId]);

    const stats = activity ? [
        ['Distance', formatDistance(activity.distanceMetres)],
        ['Moving time', formatDuration(activity.movingTimeSeconds)],
        ['Pace', formatPaceFromSpeed(activity.averageSpeed)],
        ['Elevation gain', formatWhole(activity.elevationGainMetres, 'm')],
        ['Heart rate', formatWhole(activity.averageHeartrate, 'bpm')],
        ['Max heart rate', formatWhole(activity.maxHeartrate, 'bpm')],
        ['Cadence', activity.averageCadence === null || activity.averageCadence === undefined ? '—' : `${Math.round(activity.averageCadence * 2)} spm`],
        ['Calories', formatWhole(activity.calories, 'kcal')],
    ] : [];
    const toggleMetric = (metric) => {
        setSelectedMetrics((currentMetrics) => {
            const normalizedMetrics = currentMetrics.map(normalizeStreamMetric).filter((currentMetric) => STREAM_METRICS[currentMetric]);
            return normalizedMetrics.includes(metric)
                ? normalizedMetrics.filter((currentMetric) => currentMetric !== metric)
                : [...normalizedMetrics, metric];
        });
    };
    const activeMetrics = selectedMetrics.map(normalizeStreamMetric).filter((metric, index, metrics) => STREAM_METRICS[metric] && metrics.indexOf(metric) === index);
    const streamSeries = activity ? activeMetrics.map((metric) => ({
        metric,
        points: metric === 'elevation_gain' ? elevationGainPoints(activity.streams || [], activity.elevationGainMetres) : metric === 'grade_percent' ? gradePercentPoints(activity.streams || []) : (activity.streams || [])
            .map((sample, index) => ({
                xValue: streamXValue(sample, index),
                value: streamMetricValue(sample, metric),
            }))
            .filter((point) => point.value !== null),
    })) : [];
    const streamPoints = streamSeries.flatMap((graphSeries) => graphSeries.points);
    const hasStreamData = streamPoints.length > 1;
    const streamDomainStart = hasStreamData ? Math.min(...streamPoints.map((point) => point.xValue)) : 0;
    const streamDomainEnd = hasStreamData ? Math.max(...streamPoints.map((point) => point.xValue)) : 1;
    // Whether this activity has any stream samples at all, independent of
    // which metrics happen to be selected - an activity synced with no
    // streams (e.g. a manual entry) has no chart to show no matter what's
    // picked, so the whole panel (heading, metric tabs, chart) is skipped
    // rather than showing tabs for a chart that can never render.
    const hasAnyStreamSamples = (activity?.streams || []).length > 0;
    // Same "at least 2 GPS samples" threshold ActivityRouteMap itself uses
    // to decide whether it has an actual route to draw - checked here too
    // so the panel's heading doesn't show above a map that will just render
    // its own "no map samples" message underneath.
    const hasAnyRouteSamples = (activity?.streams || [])
        .filter((sample) => sample.latitude !== null && sample.latitude !== undefined && sample.longitude !== null && sample.longitude !== undefined)
        .length >= 2;

    return (
        <main className="activity-detail">
            {isLoading ? <p className="activity-detail-message">Loading activity...</p> : error ? <p className="activity-detail-message">{error}</p> : activity && <>
                <header className="activity-detail-heading">
                    <span className="activity-detail-eyebrow">{activity.type}</span>
                    <div className="activity-detail-heading-row">
                        <h3>{activity.name}</h3>
                        <p>{formatDate(activity.date)}</p>
                        {activity.sourceUrl && <a
                            className="activity-detail-source-link"
                            href={activity.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                src={require("../images/api_logo_pwrd_by_strava_stack_light.png")}
                                alt="View on Strava"
                            />
                        </a>}
                    </div>
                </header>
                <section className="activity-detail-stats" aria-label="Activity statistics">
                    {stats.map(([label, value]) => <article key={label}><span>{label}</span><strong>{value}</strong></article>)}
                </section>
                {hasAnyRouteSamples && <section className="activity-detail-panel">
                    <div className="activity-detail-panel-heading">
                        <h3 style={{ margin: '2px 0px' }}>Map</h3>
                    </div>
                    <ActivityRouteMap activity={activity} highlightedXValue={highlightedRouteXValue} onHoverXValue={setHighlightedRouteXValue} />
                </section>}
                {hasAnyStreamSamples && <section className="activity-detail-panel">
                    <div className="activity-detail-panel-heading">
                        <div className="activity-detail-tabs" aria-label="Stream metrics">
                            {Object.entries(STREAM_METRICS).map(([metric, config]) => <button key={metric} type="button" aria-pressed={activeMetrics.includes(metric)} className={activeMetrics.includes(metric) ? 'selected' : ''} onClick={() => toggleMetric(metric)}><i style={{ background: config.colour }} />{config.label}</button>)}
                        </div>
                    </div>
                    {activeMetrics.length === 0 ? <p className="activity-detail-empty">Select a stream metric to view this activity.</p> : hasStreamData ? <MultiMetricChart series={streamSeries} metricsConfig={STREAM_METRICS} selectedMetrics={activeMetrics} formatValueWithUnit={formatStreamValue} formatXAxisLabel={formatStreamXAxisLabel} formatTooltipLabel={formatStreamXAxisLabel} onHoverPoint={(point) => setHighlightedRouteXValue(point?.xValue ?? null)} externalHoverXValue={highlightedRouteXValue} xDomainStart={streamDomainStart} xDomainEnd={streamDomainEnd} /> : <p className="activity-detail-empty">No stream samples stored for the selected metrics.</p>}
                </section>}
            </>}
        </main>
    );
};

export default ActivityDetail;